import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DeleteIcon,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
} from 'opensolar-ui'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDataProvider, useErrorHandling, useNotify, useTranslate } from 'react-admin'
import { FILE_SIZE_LIMITS } from '../../../constants/fileSizeLimits'
import { validateFileUrl } from '../../../utils/fileValidations'
import DeleteExpoStandResourceDialog from './DeleteExpoStandResourceDialog'

const PDF_MAX_SIZE = FILE_SIZE_LIMITS.PDF_DOCUMENT
const PDF_MIME_TYPE = 'application/pdf'

interface ExpoStandResource {
  id: number
  title: string
  component_content: number | null
  resource_is_file_upload: boolean
  resource_pdf: string | null
  resource_url: string | null
}

interface ShowcaseProduct {
  id: number
  title: string
}

interface PropTypes {
  standId?: number
  disabled?: boolean
}

const formatMaxSize = () => `${(PDF_MAX_SIZE / (1024 * 1024)).toFixed(0)}MB`

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && typeof (error as Error).message === 'string') {
    const message = (error as Error).message
    if (message && message !== 'Failed to fetch' && !message.startsWith('ra.notification.')) {
      return message
    }
  }
  return fallback
}

const notifyValidationError = (
  notify: ReturnType<typeof useNotify>,
  translate: ReturnType<typeof useTranslate>,
  errorKey: string,
  errorParams?: Record<string, string>
) => {
  notify(errorParams ? translate(errorKey, errorParams) : translate(errorKey), 'warning')
}

const ExpoStandResourcesManager: FC<PropTypes> = ({ standId, disabled }) => {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const errorHandler = useErrorHandling()

  const [resources, setResources] = useState<ExpoStandResource[]>([])
  const [products, setProducts] = useState<ShowcaseProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ExpoStandResource | null>(null)

  const [title, setTitle] = useState('')
  const [productId, setProductId] = useState<string>('')
  const [isFileUpload, setIsFileUpload] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [url, setUrl] = useState('')

  const productTitleById = useMemo(() => {
    const map: Record<number, string> = {}
    products.forEach((product) => {
      map[product.id] = product.title
    })
    return map
  }, [products])

  const loadResources = useCallback(() => {
    if (!standId) return
    setLoading(true)
    dataProvider
      .getList('expo_stand_resources', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'created_date', order: 'ASC' },
        filter: { expo_stand: standId },
      })
      .then(({ data }) => setResources(data as ExpoStandResource[]))
      .catch((error) => {
        errorHandler.data_provider(error as Error, {
          msg: translate('Unable to load resources'),
          notify: true,
        })
      })
      .finally(() => setLoading(false))
  }, [dataProvider, errorHandler, standId, translate])

  const loadProducts = useCallback(() => {
    if (!standId) return
    dataProvider
      .getList('component_content', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'title', order: 'ASC' },
        filter: { is_archived: false },
      })
      .then(({ data }) => setProducts(data as ShowcaseProduct[]))
      .catch((error) => {
        errorHandler.data_provider(error as Error, {
          msg: translate('Unable to load showcase products'),
          notify: true,
        })
        setProducts([])
      })
  }, [dataProvider, errorHandler, standId, translate])

  useEffect(() => {
    loadResources()
    loadProducts()
  }, [loadResources, loadProducts])

  const resetForm = () => {
    setTitle('')
    setProductId('')
    setIsFileUpload(false)
    setFile(null)
    setUrl('')
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null
    setFile(selected)
  }

  const handleAdd = async () => {
    if (!standId) return

    if (!title.trim()) {
      notify(translate('Please enter a title for the resource'), 'warning')
      return
    }

    if (isFileUpload) {
      if (!file) {
        notify(translate('Please select a PDF file to upload'), 'warning')
        return
      }
      if (file.type !== PDF_MIME_TYPE) {
        notify(translate('Only PDF files are allowed'), 'warning')
        return
      }
      if (file.size > PDF_MAX_SIZE) {
        notify(
          translate(
            'The maximum file size for this item is %{maxSize}. Please resize the file to be within the advised limit.',
            { maxSize: formatMaxSize() }
          ),
          'warning'
        )
        return
      }
    } else {
      if (!url.trim()) {
        notify(translate('Please enter a public PDF URL'), 'warning')
        return
      }
      const validation = await validateFileUrl(url, {
        maxSizeBytes: PDF_MAX_SIZE,
        allowedMimeTypes: [PDF_MIME_TYPE],
      })
      if (!validation.valid) {
        const errorKey = validation.error || 'Resource PDF URL is not valid'
        notifyValidationError(notify, translate, errorKey, validation.errorParams)
        return
      }
    }

    const data: Record<string, any> = {
      expo_stand: standId,
      title: title.trim(),
      component_content: productId ? Number(productId) : null,
      resource_is_file_upload: isFileUpload,
    }
    if (isFileUpload && file) {
      data.resource_pdf = [{ rawFile: file, title: file.name }]
    } else {
      data.resource_url = url.trim()
    }

    setSubmitting(true)
    try {
      await dataProvider.create('expo_stand_resources', { data })
      notify(translate('Resource added'), 'info')
      resetForm()
      loadResources()
    } catch (error) {
      errorHandler.data_provider(error as Error, {
        msg: getApiErrorMessage(error, translate('Unable to add resource')),
        notify: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget || !standId) return
    try {
      await dataProvider.delete('expo_stand_resources', {
        id: deleteTarget.id,
        meta: { expo_stand: standId },
      } as Parameters<typeof dataProvider.delete>[1])
      notify(translate('Resource deleted'), 'info')
      loadResources()
    } catch (error) {
      errorHandler.data_provider(error as Error, {
        msg: translate('Unable to delete resource'),
        notify: true,
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  if (!standId) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translate('Save the Expo Stand first to manage resources.')}
      </Typography>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <div>
        <Typography variant="subtitle2">{translate('Uploaded Resources')}</Typography>
        {loading ? (
          <CircularProgress size={20} />
        ) : resources.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {translate('No resources added yet.')}
          </Typography>
        ) : (
          resources.map((resource) => {
            const fileUrl = resource.resource_is_file_upload ? resource.resource_pdf : resource.resource_url
            const productLabel =
              resource.component_content && productTitleById[resource.component_content]
                ? productTitleById[resource.component_content]
                : translate('Other Products')
            return (
              <Box
                key={resource.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ borderBottom: '1px solid #eee', padding: '4px 0' }}
              >
                <Box display="flex" flexDirection="column">
                  {fileUrl ? (
                    <Link href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {resource.title}
                    </Link>
                  ) : (
                    <span>{resource.title}</span>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {productLabel}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label={translate('Delete')}
                  data-testid={`delete-resource-${resource.id}`}
                  onClick={() => setDeleteTarget(resource)}
                >
                  <DeleteIcon width="20" height="20" />
                </IconButton>
              </Box>
            )
          })
        )}
      </div>

      {!disabled && (
        <Box display="flex" flexDirection="column" gridGap={8} style={{ borderTop: '1px solid #ddd', paddingTop: 12 }}>
          <Typography variant="subtitle2">{translate('Add Resource')}</Typography>
          <TextField
            label={translate('Title')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
          />
          <TextField
            select
            label={translate('Map to Showcase product (optional)')}
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            fullWidth
            helperText={translate('Unmapped resources appear under "Other Products".')}
          >
            <MenuItem value="">{translate('Other Products')}</MenuItem>
            {products.map((product) => (
              <MenuItem key={product.id} value={String(product.id)}>
                {product.title}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox checked={isFileUpload} onChange={(event) => setIsFileUpload(event.target.checked)} />
            }
            label={translate('Upload PDF')}
          />
          {isFileUpload ? (
            <input type="file" accept=".pdf" onChange={handleFileChange} />
          ) : (
            <TextField
              label={translate('Public PDF URL')}
              placeholder={translate('Insert link to online hosted PDF')}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              fullWidth
            />
          )}
          <Box>
            <Button variant="contained" color="primary" disabled={submitting} onClick={handleAdd}>
              {submitting ? <CircularProgress size={20} /> : translate('Add Resource')}
            </Button>
          </Box>
        </Box>
      )}

      <DeleteExpoStandResourceDialog
        resource={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  )
}

export default ExpoStandResourcesManager
